import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface PaymentRow {
  readonly paymentId: string;
  readonly paymentCode: string;
  readonly saasTenantName: string | null;
  readonly saasTenantCode: string | null;
  readonly paymentMethod: string;
  readonly status: string;
  readonly currency: string;
  readonly amount: string;
  readonly instrumentMasked: string | null;
}

const PAYMENT_METHODS = ['ONLINE', 'OFFLINE', 'BANK_TRANSFER', 'UPI', 'CARD', 'GATEWAY', 'MANUAL_RECEIPT'];
const PAYMENT_STATUSES = ['PENDING', 'VERIFIED', 'FAILED', 'REVERSED', 'REFUNDED'];
const PAGE = 25;

/**
 * M44 §13 Payment Tracking Dashboard (§28). Commercial operators record
 * payments against invoices, verify and reverse them. Viewing needs
 * M44_PAYMENT_VIEW; record / verify need M44_PAYMENT_MANAGE; reverse needs
 * M44_PAYMENT_REVERSE (SoD). Instruments are masked (§27).
 */
export function PaymentTrackingDashboard(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [methodFilter, setMethodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [invoiceId, setInvoiceId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('ONLINE');
  const [amount, setAmount] = useState('0');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (methodFilter) params.set('paymentMethod', methodFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: PaymentRow[]; total: number }>(`/v1/billing/payments?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, methodFilter, statusFilter, search, page, reload]);

  const record = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!invoiceId.trim()) { setActionError('An invoice id is required.'); return; }
    try {
      await apiRequest('/v1/billing/payments', {
        method: 'POST',
        body: { invoiceId: invoiceId.trim(), paymentMethod, amount: Number(amount) || 0 },
        token,
      });
      setNotice('Payment recorded.');
      setInvoiceId('');
      setAmount('0');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to manage payments (M44_PAYMENT_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const act = async (id: string, verb: string, perm: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/billing/payments/${id}/${verb}`, { method: 'POST', body: {}, token });
      setNotice(`Payment ${verb} succeeded.`);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(`You are not authorised for this action (${perm} required).`);
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="payment-tracking-dashboard">
      <h1>Payment Tracking Dashboard</h1>

      <section aria-label="payment-record">
        <h2>Record Payment</h2>
        <form aria-label="payment-form" onSubmit={(e) => void record(e)}>
          <RecordPicker<{ invoiceId: string; invoiceNumber: string }>
            endpoint="/v1/billing/subscription-invoices?pageSize=500"
            idField="invoiceId"
            label="Invoice"
            testId="field-invoice-id"
            value={invoiceId}
            optionLabel={(i) => i.invoiceNumber}
            onPick={setInvoiceId}
            emptyText="No invoices yet"
          />
          <label>Method
            <select aria-label="field-payment-method" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <label>Amount<input aria-label="field-amount" value={amount} onChange={(e) => setAmount(e.target.value)} /></label>
          <button type="submit" aria-label="submit-payment">Record</button>
        </form>
        {actionError && <p role="alert" aria-label="payment-action-error">{actionError}</p>}
        {notice && <p aria-label="payment-notice">{notice}</p>}
      </section>

      <section aria-label="payment-filters">
        <label>Method
          <select aria-label="filter-method" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="payment-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="payment-total">{total} payment(s).</p>
          <table aria-label="payment-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Customer</th><th scope="col">Method</th><th scope="col">Instrument</th><th scope="col">Amount</th><th scope="col">Status</th><th scope="col">Actions</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7}>No payments.</td></tr>
              ) : rows.map((p) => (
                <tr key={p.paymentId} data-payment={p.paymentId}>
                  <td aria-label={`payment-code-${p.paymentId}`}>{p.paymentCode}</td>
                  <td>{personLabel(p.saasTenantName, p.saasTenantCode) ?? '—'}</td>
                  <td>{p.paymentMethod}</td>
                  <td>{p.instrumentMasked ?? '—'}</td>
                  <td>{p.currency} {money(Number(p.amount))}</td>
                  <td aria-label={`payment-status-${p.paymentId}`}>{p.status}</td>
                  <td>
                    <button type="button" aria-label={`verify-${p.paymentId}`} onClick={() => void act(p.paymentId, 'verify', 'M44_PAYMENT_MANAGE')}>Verify</button>
                    <button type="button" aria-label={`reverse-${p.paymentId}`} onClick={() => void act(p.paymentId, 'reverse', 'M44_PAYMENT_REVERSE')}>Reverse</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="payment-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
